import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageHeader from "../components/PageHeader";
import TableToolbar from "../components/TableToolbar";
import DataTable from "../components/DataTable";
import Toast, { useToast } from "../components/Toast";
import {
  obtenerEmpresas,
  crearEmpresa,
  actualizarEmpresa,
  obtenerCertificadosStatus,
  subirCertificados,
  descargarCertificado,
  obtenerLogosStatus,
  subirLogos,
} from "../services/empresaService";

const EMPTY_FORM = {
  EMPRESA: "",
  NOMBRE_EMPRESA: "",
  CALLE_EMPRESA: "",
  COLONIA_EMPRESA: "",
  NUM_EXT_EMPRESA: "",
  NUM_INT_EMPRESA: "",
  CP_EMPRESA: "",
  TELEFONOS_EMPRESA: "",
  CIUDAD_EMPRESA: "",
  ESTADO_EMPRESA: "",
  RFC_EMPRESA: "",
  PAIS: "México",
  REGIMEN: "",
  RegimenClave: "",
  Estatus: true,
};

// Catálogo SAT c_RegimenFiscal (mismo usado en ClienteForm.js para régimen del receptor)
const REGIMEN_FISCAL_OPTIONS = [
  { value: "601", label: "General de Ley Personas Morales" },
  { value: "603", label: "Personas Morales con Fines no Lucrativos" },
  { value: "605", label: "Sueldos y Salarios e Ingresos Asimilados a Salarios" },
  { value: "606", label: "Arrendamiento" },
  { value: "607", label: "Régimen de Enajenación o Adquisición de Bienes" },
  { value: "608", label: "Demás ingresos" },
  { value: "610", label: "Residentes en el Extranjero sin Establecimiento Permanente en México" },
  { value: "611", label: "Ingresos por Dividendos (socios y accionistas)" },
  { value: "612", label: "Personas Físicas con Actividades Empresariales y Profesionales" },
  { value: "614", label: "Ingresos por intereses" },
  { value: "615", label: "Régimen de los ingresos por obtención de premios" },
  { value: "616", label: "Sin obligaciones fiscales" },
  { value: "620", label: "Sociedades Cooperativas de Producción que optan por diferir sus ingresos" },
  { value: "621", label: "Incorporación Fiscal" },
  { value: "622", label: "Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras" },
  { value: "623", label: "Opcional para Grupos de Sociedades" },
  { value: "624", label: "Coordinados" },
  { value: "625", label: "Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas" },
  { value: "626", label: "Régimen Simplificado de Confianza" },
];

// El backend serializa en camelCase de ASP.NET Core, pero como el modelo usa
// nombres ALL_CAPS con guion bajo (NOMBRE_EMPRESA), el resultado queda con
// mezcla rara de mayúsculas (ej. "nombrE_EMPRESA") — normalizamos todo a
// minúsculas antes de leer, igual que ya hace FacturaPrint40.jsx.
const norm = (obj) => {
  if (!obj || typeof obj !== "object") return {};
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k.toLowerCase(), v]));
};

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

export default function EmpresasPage() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Modal certificados
  const [certModal, setCertModal] = useState(null); // { empresa, nombre }
  const [certStatus, setCertStatus] = useState(null); // { tieneCer, tieneKey, tienePwd, sinParametros }
  const [cerFile, setCerFile] = useState(null);
  const [keyFile, setKeyFile] = useState(null);
  const [pwdKey, setPwdKey] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [usuarioPac, setUsuarioPac] = useState("");
  const [passwordPac, setPasswordPac] = useState("");
  const [showPacPwd, setShowPacPwd] = useState(false);
  const [urlTimbrado40, setUrlTimbrado40] = useState("");
  const [urlTimbrado40Pruebas, setUrlTimbrado40Pruebas] = useState("");
  const [urlCancelacion, setUrlCancelacion] = useState("");
  const [urlCancelacionPruebas, setUrlCancelacionPruebas] = useState("");
  const [ambientePruebas, setAmbientePruebas] = useState(false);
  const [savingCert, setSavingCert] = useState(false);

  const [logosModal, setLogosModal] = useState(null);
  const [logosStatus, setLogosStatus] = useState(null);
  const [logoPrincipalFile, setLogoPrincipalFile] = useState(null);
  const [logoSecundarioFile, setLogoSecundarioFile] = useState(null);
  const [savingLogos, setSavingLogos] = useState(false);

  const { toasts, closeToast, error: toastError, success: toastSuccess } = useToast();

  const cargar = useCallback(() => {
    setLoading(true);
    obtenerEmpresas()
      .then(({ data }) => {
        const lista = Array.isArray(data) ? data : (data?.data ?? data?.Data ?? []);
        setRows(lista.map((e) => {
          const n = norm(e);
          return {
            id: n.id,
            empresa: n.empresa,
            nombre: n.nombre_empresa ?? "",
            rfc: n.rfc_empresa ?? "",
            ciudad: n.ciudad_empresa ?? "",
            estado: n.estado_empresa ?? "",
            estatus: n.estatus ?? true,
            raw: e,
          };
        }));
      })
      .catch((e) => toastError(e?.response?.data?.title || e?.message || "No se pudieron cargar las empresas"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      String(r.empresa).toLowerCase().includes(q) ||
      r.nombre.toLowerCase().includes(q) ||
      r.rfc.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (row) => {
    const n = norm(row.raw);
    setForm({
      Id: n.id,
      EMPRESA: n.empresa ?? "",
      NOMBRE_EMPRESA: n.nombre_empresa ?? "",
      CALLE_EMPRESA: n.calle_empresa ?? "",
      COLONIA_EMPRESA: n.colonia_empresa ?? "",
      NUM_EXT_EMPRESA: n.num_ext_empresa ?? "",
      NUM_INT_EMPRESA: n.num_int_empresa ?? "",
      CP_EMPRESA: n.cp_empresa ?? "",
      TELEFONOS_EMPRESA: n.telefonos_empresa ?? "",
      CIUDAD_EMPRESA: n.ciudad_empresa ?? "",
      ESTADO_EMPRESA: n.estado_empresa ?? "",
      RFC_EMPRESA: n.rfc_empresa ?? "",
      PAIS: n.pais ?? "",
      REGIMEN: n.regimen ?? "",
      RegimenClave: n.regimenclave ?? "",
      Estatus: n.estatus ?? true,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const isEdit = !!form.Id;
      const payload = { ...form, EMPRESA: Number(form.EMPRESA) || 0 };
      if (isEdit) {
        await actualizarEmpresa(form.Id, payload);
        toastSuccess("Empresa actualizada correctamente.");
      } else {
        await crearEmpresa(payload);
        toastSuccess("Empresa creada correctamente.");
      }
      setShowModal(false);
      cargar();
    } catch (err) {
      const d = err?.response?.data;
      if (d?.errors && typeof d.errors === "object") {
        toastError(Object.entries(d.errors).map(([k, arr]) => `${k}: ${(arr ?? []).join(" ")}`).join(" | "));
      } else {
        toastError(d?.title || d?.message || err?.message || "No se pudo guardar.");
      }
    } finally {
      setSaving(false);
    }
  };

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const setBool = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.checked }));

  const openCertModal = async (row) => {
    setCerFile(null);
    setKeyFile(null);
    setPwdKey("");
    setShowPwd(false);
    setUsuarioPac("");
    setPasswordPac("");
    setShowPacPwd(false);
    setUrlTimbrado40("");
    setUrlTimbrado40Pruebas("");
    setUrlCancelacion("");
    setUrlCancelacionPruebas("");
    setAmbientePruebas(false);
    setCertStatus(null);
    setCertModal({ empresa: row.empresa, nombre: row.nombre });
    try {
      const { data } = await obtenerCertificadosStatus(row.empresa);
      setCertStatus(data);
      setPwdKey(data.pwdKey || "");
      setUsuarioPac(data.usuarioPac || "");
      setPasswordPac(data.passwordPac || "");
      setUrlTimbrado40(data.urlTimbrado40 || "");
      setUrlTimbrado40Pruebas(data.urlTimbrado40Pruebas || "");
      setUrlCancelacion(data.urlCancelacion || "");
      setUrlCancelacionPruebas(data.urlCancelacionPruebas || "");
      setAmbientePruebas(data.ambientePruebas || false);
    } catch {
      setCertStatus({ tieneCer: false, tieneKey: false, tienePwd: false, pwdKey: "", usuarioPac: "", passwordPac: "", urlTimbrado40: "", urlTimbrado40Pruebas: "", urlCancelacion: "", urlCancelacionPruebas: "", ambientePruebas: false, sinParametros: false });
    }
  };

  const handleDescargar = async (tipo) => {
    try {
      const { data } = await descargarCertificado(certModal.empresa, tipo);
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `empresa${certModal.empresa}.${tipo}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toastError(`No se pudo descargar el archivo .${tipo}.`);
    }
  };

  const handleSubirCerts = async () => {
    setSavingCert(true);
    try {
      await subirCertificados(certModal.empresa, cerFile, keyFile, pwdKey, usuarioPac, passwordPac,
        urlTimbrado40, urlTimbrado40Pruebas, urlCancelacion, urlCancelacionPruebas, ambientePruebas);
      toastSuccess("Datos guardados en la base de datos.");
      const { data } = await obtenerCertificadosStatus(certModal.empresa);
      setCertStatus(data);
      setCerFile(null);
      setKeyFile(null);
      setPwdKey(data.pwdKey || "");
      setUsuarioPac(data.usuarioPac || "");
      setPasswordPac(data.passwordPac || "");
      setUrlTimbrado40(data.urlTimbrado40 || "");
      setUrlTimbrado40Pruebas(data.urlTimbrado40Pruebas || "");
      setUrlCancelacion(data.urlCancelacion || "");
      setUrlCancelacionPruebas(data.urlCancelacionPruebas || "");
      setAmbientePruebas(data.ambientePruebas || false);
    } catch (err) {
      toastError(err?.response?.data?.message || err?.message || "No se pudieron guardar los datos.");
    } finally {
      setSavingCert(false);
    }
  };

  const openLogosModal = async (row) => {
    setLogoPrincipalFile(null);
    setLogoSecundarioFile(null);
    setLogosStatus(null);
    setLogosModal({ empresa: row.empresa, nombre: row.nombre });
    try {
      const { data } = await obtenerLogosStatus(row.empresa);
      setLogosStatus(data);
    } catch {
      setLogosStatus({ tieneLogoPrincipal: false, tieneLogoSecundario: false });
    }
  };

  const handleSubirLogos = async () => {
    if (!logoPrincipalFile && !logoSecundarioFile) return;
    setSavingLogos(true);
    try {
      await subirLogos(logosModal.empresa, logoPrincipalFile, logoSecundarioFile);
      toastSuccess("Logos guardados correctamente.");
      const { data } = await obtenerLogosStatus(logosModal.empresa);
      setLogosStatus(data);
      setLogoPrincipalFile(null);
      setLogoSecundarioFile(null);
    } catch (err) {
      toastError(err?.response?.data?.message || err?.message || "No se pudieron guardar los logos.");
    } finally {
      setSavingLogos(false);
    }
  };

  const columns = [
    { key: "empresa", title: "Código", style: { width: 80 } },
    { key: "nombre", title: "Nombre" },
    { key: "rfc", title: "RFC", style: { width: 130 } },
    { key: "ciudad", title: "Ciudad", style: { width: 130 } },
    { key: "estado", title: "Estado", style: { width: 130 } },
    {
      key: "estatus", title: "Estatus", style: { width: 100 },
      render: (r) => (
        <span className={`lmd-badge ${r.estatus ? "lmd-badge-success" : "lmd-badge-neutral"}`}>
          {r.estatus ? "Vigente" : "Baja"}
        </span>
      ),
    },
    {
      key: "acciones", title: "", style: { width: 260 },
      render: (r) => (
        <div style={{ display: "flex", gap: 6 }}>
          <button className="lmd-btn lmd-btn-warning lmd-btn-sm" onClick={() => openEdit(r)}>Editar</button>
          <button className="lmd-btn lmd-btn-secondary lmd-btn-sm" onClick={() => openCertModal(r)}>Certificados</button>
          <button className="lmd-btn lmd-btn-secondary lmd-btn-sm" onClick={() => openLogosModal(r)}>Logos</button>
        </div>
      ),
    },
  ];

  return (
    <>
      <Toast toasts={toasts} onClose={closeToast} />
      <PageHeader title="Empresas" subtitle="Mantenimiento de empresas del sistema" />

      <div className="lmd-card">
        <TableToolbar
          searchPlaceholder="Buscar por código, nombre o RFC..."
          searchValue={search}
          onSearchChange={setSearch}
          onSearch={() => {}}
          onAdd={openNew}
          addText="Nueva Empresa"
        />

        {loading && (
          <div className="lmd-loading-bar">
            <div className="lmd-spinner" /> Cargando empresas...
          </div>
        )}

        <DataTable columns={columns} rows={filtered} keyField="id" />

        <div className="lmd-pagination">
          <span className="lmd-pagination-info">
            Total: <strong>{filtered.length}</strong> empresas
          </span>
        </div>
      </div>

      {logosModal && (
        <div className="lmd-modal-overlay" onClick={() => setLogosModal(null)}>
          <div className="lmd-modal" style={{ maxWidth: 500 }} onClick={(e) => e.stopPropagation()}>
            <div className="lmd-modal-header">
              <h3 className="lmd-modal-title">Logos — {logosModal.nombre}</h3>
              <button className="lmd-modal-close" onClick={() => setLogosModal(null)}><CloseIcon /></button>
            </div>

            <div className="lmd-modal-body" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {logosStatus === null && <p style={{ color: "var(--clr-text-muted)" }}>Consultando estado...</p>}

              {logosStatus && (
                <>
                  <div style={{ display: "flex", gap: 12 }}>
                    {[
                      { ok: logosStatus.tieneLogoPrincipal,  si: "Logo principal cargado",  no: "Sin logo principal" },
                      { ok: logosStatus.tieneLogoSecundario, si: "Logo secundario cargado", no: "Sin logo secundario" },
                    ].map((item, i) => (
                      <div key={i} style={{ flex: 1, padding: "12px 8px", borderRadius: 8, border: "1px solid var(--clr-border)", textAlign: "center" }}>
                        <div style={{ fontSize: 22, marginBottom: 4 }}>{item.ok ? "✓" : "—"}</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: item.ok ? "var(--clr-success)" : "var(--clr-text-muted)" }}>
                          {item.ok ? item.si : item.no}
                        </div>
                      </div>
                    ))}
                  </div>

                  {(logosStatus.tieneLogoPrincipal || logosStatus.tieneLogoSecundario) && (
                    <div style={{ display: "flex", gap: 16, justifyContent: "center" }}>
                      {logosStatus.tieneLogoPrincipal && (
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 11, color: "var(--clr-text-muted)", marginBottom: 4 }}>PRINCIPAL (izquierda)</div>
                          <img
                            src={`${process.env.REACT_APP_API_BASE_URL || ""}/api/empresas/${logosModal.empresa}/logo/principal?t=${Date.now()}`}
                            alt="Logo principal"
                            style={{ maxHeight: 70, maxWidth: 160, objectFit: "contain", border: "1px solid var(--clr-border)", borderRadius: 6, padding: 4 }}
                          />
                        </div>
                      )}
                      {logosStatus.tieneLogoSecundario && (
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 11, color: "var(--clr-text-muted)", marginBottom: 4 }}>SECUNDARIO (derecha)</div>
                          <img
                            src={`${process.env.REACT_APP_API_BASE_URL || ""}/api/empresas/${logosModal.empresa}/logo/secundario?t=${Date.now()}`}
                            alt="Logo secundario"
                            style={{ maxHeight: 70, maxWidth: 160, objectFit: "contain", border: "1px solid var(--clr-border)", borderRadius: 6, padding: 4 }}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="lmd-label">Logo principal{logosStatus.tieneLogoPrincipal ? " — reemplazar" : ""}</label>
                    <input type="file" accept="image/jpeg,image/png,image/svg+xml" className="lmd-input"
                      style={{ padding: "6px 10px" }}
                      onChange={(e) => setLogoPrincipalFile(e.target.files[0] || null)} />
                    <div style={{ fontSize: 11, color: "var(--clr-text-muted)", marginTop: 4 }}>JPG, PNG o SVG · máx. 500 KB · aparece en el lado izquierdo</div>
                  </div>
                  <div>
                    <label className="lmd-label">Logo secundario{logosStatus.tieneLogoSecundario ? " — reemplazar" : ""}</label>
                    <input type="file" accept="image/jpeg,image/png,image/svg+xml" className="lmd-input"
                      style={{ padding: "6px 10px" }}
                      onChange={(e) => setLogoSecundarioFile(e.target.files[0] || null)} />
                    <div style={{ fontSize: 11, color: "var(--clr-text-muted)", marginTop: 4 }}>JPG, PNG o SVG · máx. 500 KB · aparece en el lado derecho</div>
                  </div>
                </>
              )}
            </div>

            <div className="lmd-modal-footer">
              <button className="lmd-btn lmd-btn-secondary" onClick={() => setLogosModal(null)}>Cerrar</button>
              {logosStatus && (
                <button className="lmd-btn lmd-btn-primary"
                  disabled={savingLogos || (!logoPrincipalFile && !logoSecundarioFile)}
                  onClick={handleSubirLogos}>
                  {savingLogos ? "Guardando..." : "Guardar en BD"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {certModal && (
        <div className="lmd-modal-overlay" onClick={() => setCertModal(null)}>
          <div className="lmd-modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="lmd-modal-header">
              <h3 className="lmd-modal-title">Certificados — {certModal.nombre}</h3>
              <button className="lmd-modal-close" onClick={() => setCertModal(null)}><CloseIcon /></button>
            </div>

            <div className="lmd-modal-body" style={{ display: "flex", flexDirection: "column", gap: 20 }}>

              {certStatus === null && (
                <p style={{ color: "var(--clr-text-muted)" }}>Consultando estado...</p>
              )}

              {certStatus?.sinParametros && (
                <div style={{ padding: "12px 16px", borderRadius: 8, background: "#fff8e1", border: "1px solid #f9a825", color: "#5d4037", fontSize: 14 }}>
                  Esta empresa no tiene parámetros de facturación electrónica configurados en la base de datos.
                  Configúralos antes de subir certificados.
                </div>
              )}

              {certStatus && !certStatus.sinParametros && (
                <div style={{ display: "flex", gap: 12 }}>
                  {[
                    { ok: certStatus.tieneCer, si: "Certificado .cer cargado", no: "Sin certificado .cer" },
                    { ok: certStatus.tieneKey, si: "Llave .key cargada",       no: "Sin llave .key" },
                    { ok: certStatus.tienePwd, si: "Contraseña guardada",       no: "Sin contraseña" },
                  ].map((item, i) => (
                    <div key={i} style={{ flex: 1, padding: "12px 8px", borderRadius: 8, border: "1px solid var(--clr-border)", textAlign: "center" }}>
                      <div style={{ fontSize: 22, marginBottom: 4 }}>{item.ok ? "✓" : "—"}</div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: item.ok ? "var(--clr-success)" : "var(--clr-text-muted)" }}>
                        {item.ok ? item.si : item.no}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {certStatus && !certStatus.sinParametros && (
                <>
                  <div>
                    <label className="lmd-label">Certificado (.cer){certStatus.tieneCer ? " — reemplazar" : ""}</label>
                    <div style={{ display: "flex", gap: 8 }}>
                      <input
                        type="file" accept=".cer" className="lmd-input" style={{ padding: "6px 10px", flex: 1 }}
                        onChange={(e) => setCerFile(e.target.files[0] || null)}
                      />
                      {certStatus.tieneCer && (
                        <button type="button" className="lmd-btn lmd-btn-secondary lmd-btn-sm"
                          onClick={() => handleDescargar("cer")} title="Descargar .cer guardado">
                          ↓ .cer
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="lmd-label">Llave privada (.key){certStatus.tieneKey ? " — reemplazar" : ""}</label>
                    <div style={{ display: "flex", gap: 8 }}>
                      <input
                        type="file" accept=".key" className="lmd-input" style={{ padding: "6px 10px", flex: 1 }}
                        onChange={(e) => setKeyFile(e.target.files[0] || null)}
                      />
                      {certStatus.tieneKey && (
                        <button type="button" className="lmd-btn lmd-btn-secondary lmd-btn-sm"
                          onClick={() => handleDescargar("key")} title="Descargar .key guardado">
                          ↓ .key
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="lmd-label">Contraseña del .key{certStatus.tienePwd ? " — reemplazar" : ""}</label>
                    <div style={{ position: "relative" }}>
                      <input
                        type={showPwd ? "text" : "password"}
                        className="lmd-input"
                        placeholder={certStatus.tienePwd ? "••••••••" : "Escribir contraseña"}
                        value={pwdKey}
                        onChange={(e) => setPwdKey(e.target.value)}
                        style={{ paddingRight: 40 }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPwd((v) => !v)}
                        style={{
                          position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                          background: "none", border: "none", cursor: "pointer", padding: 0,
                          color: "var(--clr-text-muted)", lineHeight: 1,
                        }}
                        title={showPwd ? "Ocultar" : "Mostrar"}
                      >
                        {showPwd ? (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                            <line x1="1" y1="1" x2="23" y2="23"/>
                          </svg>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </>
              )}

              {certStatus && !certStatus.sinParametros && (
                <>
                  <div style={{ marginTop: 4, paddingTop: 16, borderTop: "1px solid var(--clr-border)" }}>
                    <p style={{ margin: "0 0 12px", fontWeight: 600, fontSize: 13 }}>API Comercio Digital</p>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                    <input
                      type="checkbox"
                      id="amb-pruebas"
                      checked={ambientePruebas}
                      onChange={(e) => setAmbientePruebas(e.target.checked)}
                      style={{ width: 17, height: 17, cursor: "pointer", accentColor: "var(--clr-warning, #f59e0b)" }}
                    />
                    <label htmlFor="amb-pruebas" className="lmd-label" style={{ marginBottom: 0, cursor: "pointer", color: ambientePruebas ? "var(--clr-warning, #f59e0b)" : undefined }}>
                      Ambiente de pruebas activo
                    </label>
                    {ambientePruebas && (
                      <span style={{ fontSize: 11, background: "#fef3c7", color: "#92400e", borderRadius: 4, padding: "2px 8px", fontWeight: 600 }}>
                        PRUEBAS
                      </span>
                    )}
                  </div>

                  <div>
                    <label className="lmd-label">URL timbrado — producción</label>
                    <input className="lmd-input" maxLength={500} value={urlTimbrado40}
                      onChange={(e) => setUrlTimbrado40(e.target.value)} autoComplete="off"
                      placeholder="https://ws.comercio-digital.mx/timbre4/timbrarV5" />
                  </div>
                  <div>
                    <label className="lmd-label">URL timbrado — pruebas</label>
                    <input className="lmd-input" maxLength={500} value={urlTimbrado40Pruebas}
                      onChange={(e) => setUrlTimbrado40Pruebas(e.target.value)} autoComplete="off"
                      placeholder="https://pruebas.comercio-digital.mx/timbre4/timbrarV5" />
                  </div>
                  <div>
                    <label className="lmd-label">URL cancelación — producción</label>
                    <input className="lmd-input" maxLength={500} value={urlCancelacion}
                      onChange={(e) => setUrlCancelacion(e.target.value)} autoComplete="off"
                      placeholder="https://cancela.comercio-digital.mx/cancela4/cancelarUuid" />
                  </div>
                  <div>
                    <label className="lmd-label">URL cancelación — pruebas</label>
                    <input className="lmd-input" maxLength={500} value={urlCancelacionPruebas}
                      onChange={(e) => setUrlCancelacionPruebas(e.target.value)} autoComplete="off"
                      placeholder="https://pruebas.comercio-digital.mx/cancela4/cancelarUuid" />
                  </div>

                  <div>
                    <label className="lmd-label">Usuario</label>
                    <input
                      className="lmd-input"
                      maxLength={100}
                      value={usuarioPac}
                      onChange={(e) => setUsuarioPac(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  <div>
                    <label className="lmd-label">Contraseña</label>
                    <div style={{ position: "relative" }}>
                      <input
                        type={showPacPwd ? "text" : "password"}
                        className="lmd-input"
                        maxLength={100}
                        value={passwordPac}
                        onChange={(e) => setPasswordPac(e.target.value)}
                        autoComplete="new-password"
                        style={{ paddingRight: 40 }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPacPwd((v) => !v)}
                        style={{
                          position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                          background: "none", border: "none", cursor: "pointer", padding: 0,
                          color: "var(--clr-text-muted)", lineHeight: 1,
                        }}
                        title={showPacPwd ? "Ocultar" : "Mostrar"}
                      >
                        {showPacPwd ? (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                            <line x1="1" y1="1" x2="23" y2="23"/>
                          </svg>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="lmd-modal-footer">
              <button className="lmd-btn lmd-btn-secondary" onClick={() => setCertModal(null)}>Cerrar</button>
              {certStatus && !certStatus.sinParametros && (
                <button
                  className="lmd-btn lmd-btn-primary"
                  disabled={savingCert}
                  onClick={handleSubirCerts}
                >
                  {savingCert ? "Guardando..." : "Guardar en BD"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="lmd-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="lmd-modal lmd-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="lmd-modal-header">
              <h3 className="lmd-modal-title">{form.Id ? "Editar Empresa" : "Nueva Empresa"}</h3>
              <button className="lmd-modal-close" onClick={() => setShowModal(false)}><CloseIcon /></button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="lmd-modal-body">
                <div className="lmd-form-grid lmd-form-grid-2" style={{ gap: 16 }}>

                  <div>
                    <label className="lmd-label">Código de empresa *</label>
                    <input
                      type="number" className="lmd-input" required
                      value={form.EMPRESA}
                      disabled={!!form.Id}
                      onChange={setField("EMPRESA")}
                      title={form.Id ? "El código no se puede cambiar una vez creada la empresa" : ""}
                    />
                  </div>

                  <div>
                    <label className="lmd-label">Nombre de la empresa *</label>
                    <input className="lmd-input" maxLength={100} required value={form.NOMBRE_EMPRESA} onChange={setField("NOMBRE_EMPRESA")} />
                  </div>

                  <div>
                    <label className="lmd-label">RFC *</label>
                    <input className="lmd-input" maxLength={13} required value={form.RFC_EMPRESA} onChange={setField("RFC_EMPRESA")} style={{ textTransform: "uppercase" }} />
                  </div>

                  <div>
                    <label className="lmd-label">Régimen fiscal *</label>
                    <input className="lmd-input" maxLength={100} required value={form.REGIMEN} onChange={setField("REGIMEN")} />
                  </div>

                  <div>
                    <label className="lmd-label">Clave de régimen *</label>
                    <select
                      className="lmd-select"
                      style={{ width: "100%" }}
                      required
                      value={form.RegimenClave}
                      onChange={(e) => {
                        const clave = e.target.value;
                        const opt = REGIMEN_FISCAL_OPTIONS.find((o) => o.value === clave);
                        setForm((f) => ({
                          ...f,
                          RegimenClave: clave,
                          REGIMEN: opt ? opt.label : f.REGIMEN,
                        }));
                      }}
                    >
                      <option value="">-- Seleccionar --</option>
                      {REGIMEN_FISCAL_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.value} - {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="lmd-label">Teléfonos</label>
                    <input className="lmd-input" maxLength={50} value={form.TELEFONOS_EMPRESA} onChange={setField("TELEFONOS_EMPRESA")} />
                  </div>

                  <div style={{ gridColumn: "1/-1", marginTop: 8, paddingTop: 12, borderTop: "1px solid var(--clr-border)" }}>
                    <label className="lmd-label" style={{ marginBottom: 12, display: "block" }}>Domicilio fiscal</label>
                  </div>

                  <div>
                    <label className="lmd-label">Calle *</label>
                    <input className="lmd-input" maxLength={100} required value={form.CALLE_EMPRESA} onChange={setField("CALLE_EMPRESA")} />
                  </div>

                  <div>
                    <label className="lmd-label">Colonia *</label>
                    <input className="lmd-input" maxLength={100} required value={form.COLONIA_EMPRESA} onChange={setField("COLONIA_EMPRESA")} />
                  </div>

                  <div>
                    <label className="lmd-label">Núm. exterior *</label>
                    <input className="lmd-input" maxLength={20} required value={form.NUM_EXT_EMPRESA} onChange={setField("NUM_EXT_EMPRESA")} />
                  </div>

                  <div>
                    <label className="lmd-label">Núm. interior</label>
                    <input className="lmd-input" maxLength={20} value={form.NUM_INT_EMPRESA} onChange={setField("NUM_INT_EMPRESA")} />
                  </div>

                  <div>
                    <label className="lmd-label">Código postal *</label>
                    <input className="lmd-input" maxLength={6} required value={form.CP_EMPRESA} onChange={setField("CP_EMPRESA")} />
                  </div>

                  <div>
                    <label className="lmd-label">Ciudad *</label>
                    <input className="lmd-input" maxLength={50} required value={form.CIUDAD_EMPRESA} onChange={setField("CIUDAD_EMPRESA")} />
                  </div>

                  <div>
                    <label className="lmd-label">Estado *</label>
                    <input className="lmd-input" maxLength={50} required value={form.ESTADO_EMPRESA} onChange={setField("ESTADO_EMPRESA")} />
                  </div>

                  <div>
                    <label className="lmd-label">País *</label>
                    <input className="lmd-input" maxLength={100} required value={form.PAIS} onChange={setField("PAIS")} />
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 10, paddingTop: 20 }}>
                    <input type="checkbox" id="emp-status" checked={form.Estatus} onChange={setBool("Estatus")}
                      style={{ width: 17, height: 17, cursor: "pointer", accentColor: "var(--clr-primary)" }} />
                    <label htmlFor="emp-status" className="lmd-label" style={{ marginBottom: 0, cursor: "pointer" }}>
                      Empresa vigente
                    </label>
                  </div>

                </div>
              </div>

              <div className="lmd-modal-footer">
                <button type="button" className="lmd-btn lmd-btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="lmd-btn lmd-btn-primary" disabled={saving}>
                  {saving ? "Guardando..." : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
